const drawerEvents = {
    OnLineItemUpdate: 'action:cart:updateLineItem',
    onQuantityChange: 'on:QuantityChange',
    triggerCartChange: 'action:cart:updateCart',
};

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.currentLineItem;
        this.lineItemError = null;
        this.cartIdentifier = this.getAttribute('render-section');
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );

        this.sectionId =
            this.getAttribute('data-sectionId') || this.cartIdentifier;

        this.addEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.handleLineItem.bind(this)
        );

        /* Listening for this event to update cart items and show cart drawer base on products Added to cart via product-form,product-card component 
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.triggerCartChange, { detail: {}}));
            Note: Make sure to pass the `detail` @object with the data consisting of sections that can fetched via section rendering Api functionality of Shopify
        */

        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.renderCart.bind(this)
        );

        /* Listening for this event to update cart items and show cart drawer based on custom functionality of the theme 
            Scenarios: When user adds a product to cart, the cart drawer should be updated and shown
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.updateAndShowCart, { detail: {}}));
        */
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.fetchAndRenderCartItems.bind(this)
        );

        // Handle form submission to filter out free products
        this.setupCheckoutFormHandler();
    }

    setupCheckoutFormHandler() {
        // Find the form - it might be inside the drawer-cart or in a parent element
        const form = this.querySelector('form[action*="cart"]') || 
                     this.closest('cart-drawer-component')?.querySelector('form[action*="cart"]');
        if (form) {
            // Bind the handler to this instance
            if (!this._boundCheckoutHandler) {
                this._boundCheckoutHandler = (e) => {
                    this.handleCheckoutFormSubmit(e);
                };
            }
            // Remove any existing listener to avoid duplicates
            form.removeEventListener('submit', this._boundCheckoutHandler);
            form.addEventListener('submit', this._boundCheckoutHandler);
        }
    }

    handleCheckoutFormSubmit(e) {
        // Get all line items from the cart drawer
        const lineItems = this.querySelectorAll('drawer-item');
        const freeProductKeys = [];
        let hasPaidItems = false;
        
        lineItems.forEach((item) => {
            const itemKey = item.getAttribute('item-key');
            
            // Check if quantity selector is hidden (which indicates free product - price == 0)
            // In the liquid template, free products have: {% unless line_item.price == 0 %}
            const quantityWrapper = item.querySelector('.cart__drawer__quantity');
            const isFreeProduct = !quantityWrapper || quantityWrapper.style.display === 'none';
            
            if (isFreeProduct) {
                freeProductKeys.push(itemKey);
            } else {
                // This is a paid product
                hasPaidItems = true;
            }
        });

        // If there are free products and no paid items, prevent checkout
        if (freeProductKeys.length > 0 && !hasPaidItems) {
            e.preventDefault();
            e.stopPropagation();
            this.showDrawerToastMessage({
                responseObj: {
                    message: 'Free products cannot be checked out alone. Please add a paid product to your cart.',
                },
                messageType: 'error',
            });
            return false;
        }

        // Stamp PDP offer code onto the cart checkout form when present.
        if (typeof HELPER_UTIL !== 'undefined' && HELPER_UTIL.stampCheckoutWithOfferDiscount) {
            HELPER_UTIL.stampCheckoutWithOfferDiscount(
                this.closest('cart-drawer-component') || this
            );
        }
    }

    _getCartItemCount(data) {
        const cartSection = data.sections[this.cartIdentifier];
        const parseCartSectionHTML = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );

        return parseCartSectionHTML.querySelector(this.tagName).dataset
            .cartitemcount;
    }

    renderCart(e) {
        this.renderCartContents(e.detail);
        // Update dependent Elements to be render
        this.renderElementsBasedOnCartItems(e.detail);
        // Re-bind checkout handler after HTML swap + stamp offer discount.
        this.setupCheckoutFormHandler();
        if (typeof HELPER_UTIL !== 'undefined' && HELPER_UTIL.stampCheckoutWithOfferDiscount) {
            HELPER_UTIL.stampCheckoutWithOfferDiscount(
                this.closest('cart-drawer-component') || this
            );
        }
        const drawerComponent = this.closest('cart-drawer-component');
        if (this.cartIdentifier != 'cart-drawer' || !drawerComponent) return;

        if (!drawerComponent.isDrawerOpen) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
                drawerType: 'cart-drawer',
            });
        } else {
            // Qty/ATC HTML swap while open — keep chrome fully open.
            drawerComponent.classList.add('drawer-active');
            drawerComponent.setAttribute('aria-hidden', 'false');
        }
    }

    _get(property) {
        return this[property];
    }

    _set(property, value) {
        this[property] = value;
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType: messageType,
                message: responseObj.message,
            }
        );
    }

    quantityElementAction({ key, quantity }) {
        // Find the specific line item container by item-key
        // Try multiple selectors to ensure we find the correct element
        let lineItemContainer = null;
        if (key) {
            // Try drawer-item first
            lineItemContainer = this.querySelector(`drawer-item[item-key="${key}"]`);
            // If not found, try any element with item-key attribute
            if (!lineItemContainer) {
                lineItemContainer = this.querySelector(`[item-key="${key}"]`);
            }
            // If still not found, try to find by the input id which contains the key
            if (!lineItemContainer) {
                const inputWithKey = this.querySelector(`input[id*="${key}"], input[id="quantity-${key}"]`);
                if (inputWithKey) {
                    lineItemContainer = inputWithKey.closest('drawer-item') || inputWithKey.closest('[item-key]');
                }
            }
        }

        const enable = () => {
            if (lineItemContainer) {
                // Only enable quantity selector for the specific line item
                const quantitySelectors = lineItemContainer.querySelectorAll('quantity-selector');
                quantitySelectors.forEach((el) => {
                    el.setAttribute('is-disabled', 'false');
                    el.removeAttribute('is-disabled');
                    // Also enable the input and buttons
                    const input = el.querySelector('input');
                    const buttons = el.querySelectorAll('button');
                    if (input) input.disabled = false;
                    buttons.forEach(btn => btn.disabled = false);
                });
            }
            // Don't enable all as fallback - only enable the specific one
        };

        const disable = () => {
            if (lineItemContainer) {
                // Only disable quantity selector for the specific line item being updated
                const quantitySelectors = lineItemContainer.querySelectorAll('quantity-selector');
                quantitySelectors.forEach((el) => {
                    el.setAttribute('is-disabled', 'true');
                    // Also disable the input and buttons
                    const input = el.querySelector('input');
                    const buttons = el.querySelectorAll('button');
                    if (input) input.disabled = true;
                    buttons.forEach(btn => btn.disabled = true);
                });
            }
            // Don't disable all as fallback - only disable the specific one
        };

        const resetToPreviousValue = () => {
            if (lineItemContainer) {
                lineItemContainer
                    .querySelectorAll('quantity-selector input')
                    .forEach((el) => {
                        el.value = (Number(quantity) - 1).toString();
                    });
            }
        };

        return {
            enable,
            disable,
            resetToPreviousValue,
        };
    }

    updateCartTotal(data) {
        const cartTotal = data.total_price;
        const cartTotalEl = document.querySelectorAll('[cart-total]');
        [...cartTotalEl].forEach((el) => {
            el.textContent = Shopify.formatMoney(
                cartTotal,
                Shopify.money_format
            );
        });
    }

    cartCheckoutEl() {
        // const checkoutContainer = document.querySelector(
        //     '[cart-checkout-container]'
        // );
        const hideContainerClass = 'hide';
        const show = () => {
            if (this.checkoutContainer.classList.contains(hideContainerClass)) {
                this.checkoutContainer.classList.remove(hideContainerClass);
            }
        };
        const hide = () => {
            this.checkoutContainer.classList.add(hideContainerClass);
        };

        return {
            show,
            hide,
        };
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        this.dataset.cartitemcount = cartItemCount;
        if (cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
        } else {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.hideCartRecommendations,
                this
            );
        }
    }

    handleLineItem({ detail }) {
        // Make Api request to update cart drawer based on line item index
        /* Detail Object return data inform of @object as follows:
            {
            line: line Item index,
            quantity: Line item quantity
         } */
        const data = {
            id: detail.key,
            quantity: detail.quantity,
        };

        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();

        // Disable quantity selector of which quantity was increased
        this.quantityElementAction(detail).disable();

        HELPER_UTIL.fetchData({
            method: 'POST',
            url: `${window.Shopify.routes.root}cart/change.js?sections=${sectionsToRender}`,
            data: JSON.stringify(data),
            config: {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections: data.sections }
                );
            },
            onerror: (error) => {
                if (error.json) {
                    error.json().then((errorObj) => {
                        if (errorObj.status === 422) {
                            HELPER_UTIL.dispatchCustomEvent(
                                _EVENT_HELPER.triggerlineItemError,
                                this,
                                {
                                    errorObj: {
                                        lineItem: detail.key,
                                        responseObj: errorObj,
                                        messageType: 'error',
                                    },
                                }
                            );
                        }
                    });
                } else {
                    this.showDrawerToastMessage({
                        responseObj: {
                            message: 'Something went wrong.Please try again',
                        },
                        messageType: 'error',
                    });
                }
            },
            defaultCb: () => {
                this.quantityElementAction(detail).enable();
            },
        });
    }

    getHTMLFromString(dataToParse, elementToGet) {
        return new DOMParser()
            .parseFromString(dataToParse, 'text/html')
            .querySelector(elementToGet);
    }

    renderCartContents({ sections }) {
        try {
            const getSectionsToLoop = HELPER_UTIL.sectionRenderUtil(
                this.cartIdentifier
            ).getSectionsObject();
            getSectionsToLoop.forEach((sectionsObjItem) => {
                // Here sections{@object} is the data from the AJax ca;l we made to cart
                // sectionsObjItem @{object} is the class property consisting list of sections and there ids

                if (!!sections[sectionsObjItem.sectionId]) {
                    [
                        ...document.querySelectorAll(
                            sectionsObjItem.queryElement
                        ),
                    ].forEach((section) => {
                        const source = this.getHTMLFromString(
                            sections[sectionsObjItem.sectionId],
                            sectionsObjItem.queryElement
                        );
                        if (source) {
                            section.innerHTML = source.innerHTML;
                        }
                    });
                }
            });
            // Re-setup form handler after cart contents are updated
            this.setupCheckoutFormHandler();
            if (typeof HELPER_UTIL.filterCartTrending === 'function') {
                HELPER_UTIL.filterCartTrending();
            }
        } catch (error) {
            console.log(error);
        }
    }

    fetchAndRenderCartItems() {
        // Get liquid section  to render
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        // Make Ajax call to update.js and render cart
        HELPER_UTIL.fetchData({
            method: 'GET',
            url: `${window.Shopify.routes.root}cart/update.js?sections=${sectionsToRender}`,
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections: data.sections }
                );
            },
            onerror: (error) => {
                console.log(error);
            },
        });
    }

    disconnectedCallback() {
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            drawerEvents.OnLineItemUpdate,
            this.handleLineItem.bind(this)
        );
        this.removeEventListener(
            drawerEvents.onQuantityChange,
            this.handleLineItem.bind(this)
        );
        this.removeEventListener(
            drawerEvents.triggerCartChange,
            this.renderCart.bind(this)
        );
        this.removeEventListener(
            drawerEvents.updateAndShowCart,
            this.fetchAndRenderCartItems.bind(this)
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl.toast({
            message: responseObj.message,
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key'); // Unique line item key of the product in cart
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');
        [...this.removeItemElement].forEach((el) => {
            el.addEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );

            el.addEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.showLineItemError.bind(this)
        );
    }

    dispatchLineItemUpdate(e) {
        const productQuantity = e?.detail.quantity;
        let detail = {
            key: this.itemKey,
            quantity: productQuantity ? productQuantity : '0',
        };
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        let customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling.querySelector('custom-message');
        console.log(errorObj);
        // Adding  a
        // setTimeout(() => {
        customMessageComponent.showCustomMessage({
            errorMessage: errorObj.responseObj.message,
            messageType: errorObj.messageType,
        });
        // }, 0);
    }

    resetQuantitySelector() {
        this.querySelector('quantity-selector input').value =
            this.getAttribute('item-quantity');
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
        }
    }

    disconnectedCallback() {
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );
            el.removeEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);



//More checkout button animation 

class AdditionalCheckoutButtonsAnimation extends HTMLElement {
    constructor() {
        super();
        this.toggleButton = this.querySelector("[toggle-payment-buttons]")
        this.toggleButton?.addEventListener("click", this.togglePaymentButtons.bind(this))
        this.buttonsWrapper = this.querySelector("[checkout-buttons-wrapper]")

    }

    togglePaymentButtons(e) {
        e.preventDefault()
        this.toggleButton.classList.toggle("show-payment-options")
        this.setAttribute("style", `--button-wrapper-height:${this.buttonsWrapper?.firstElementChild.offsetHeight}px`)
        this.buttonsWrapper?.classList.toggle("active")
    }

}

customElements.get('additional-checkout-buttons-animation') ||
    customElements.define("additional-checkout-buttons-animation", AdditionalCheckoutButtonsAnimation)